import { useState } from 'react';

import { Box } from '@mui/material';

import StatusBar from '@/designs-dev/device/StatusBar';
import NorthStarNav, { type NavTab } from '@/north-star/NorthStarNav';
import NorthStarHome from '@/north-star/NorthStarHome';
import NorthStarTasks from '@/north-star/NorthStarTasks';
import NorthStarCare from '@/north-star/NorthStarCare';
import NorthStarClaims from '@/north-star/NorthStarClaims';
import TaskDrawer from '@/north-star/TaskDrawer';
import ClaimChat from '@/north-star/ClaimChat';
import FinancialAssessment from '@/karehero-app/care/forms/FinancialAssessment';
import { NEXT_TASKS } from '@/north-star/NorthStar.fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// The onboarded shell: a persistent nav over a swappable body. Keni is docked
// in the centre of the nav — always there to ask something (handled by the
// parent). Home shows a next-steps preview; Tasks holds the full list; Claims
// tracks each application. Starting the Attendance Allowance claim (or reviewing
// a ready one) opens the real claim form as a full-screen overlay.

export type NorthStarShellProps = {
  /** Open the Keni conversation (the docked orb). */
  onAskKeni: () => void;
  /** Reopen the claim reveal (the "see the breakdown" link on Home). */
  onSeeBreakdown: () => void;
};

const NorthStarShell = ({ onAskKeni, onSeeBreakdown }: NorthStarShellProps) => {
  const [tab, setTab] = useState<NavTab>('home');
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  // The claim overlay: 'chat' = Keni asks the claim questions (starting it);
  // 'review' = the review page for a ready-to-submit claim; null = closed.
  const [claimView, setClaimView] = useState<'chat' | 'review' | null>(null);

  const openTask = NEXT_TASKS.find((t) => t.id === openTaskId);

  // The Attendance Allowance claim task starts the Keni claim conversation;
  // every other task opens its drawer.
  const handleOpenTask = (id: string) => {
    const task = NEXT_TASKS.find((t) => t.id === id);
    if (task?.kind === 'claim') setClaimView('chat');
    else setOpenTaskId(id);
  };

  const body =
    tab === 'tasks' ? (
      <NorthStarTasks onOpenTask={handleOpenTask} doneIds={doneIds} />
    ) : tab === 'claims' ? (
      <NorthStarClaims onReviewClaim={() => setClaimView('review')} />
    ) : (
      <NorthStarHome
        onOpenTask={handleOpenTask}
        onSeeBreakdown={onSeeBreakdown}
        doneIds={doneIds}
      />
    );

  return (
    <ScreenRoot relative>
      <StatusBar />

      {/* Care owns its own layout (segments + scroll + footer); the others sit
          in a padded scroll with room for the raised centre orb. */}
      {tab === 'care' ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <NorthStarCare />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 3, pb: '40px' }}>
          {body}
        </Box>
      )}

      <NorthStarNav active={tab} onSelect={setTab} onAskKeni={onAskKeni} />

      {openTask && (
        <TaskDrawer
          task={openTask}
          onClose={() => setOpenTaskId(null)}
          onDone={(id) => setDoneIds((prev) => new Set(prev).add(id))}
        />
      )}

      {/* The claim, full-screen over the shell. Starting it is a Keni
          conversation; reviewing a ready one reuses the real financial-
          assessment review page. */}
      {claimView && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
          }}
        >
          {claimView === 'chat' ? (
            <ClaimChat
              onClose={() => setClaimView(null)}
              onDone={() => {
                setClaimView(null);
                setTab('tasks');
              }}
            />
          ) : (
            <FinancialAssessment
              initialPhase='review'
              onComplete={() => setClaimView(null)}
              onExit={() => setClaimView(null)}
            />
          )}
        </Box>
      )}
    </ScreenRoot>
  );
};

NorthStarShell.displayName = 'NorthStarShell';
export default NorthStarShell;
